import { ChangeEvent, useState } from 'react';

import Dropdown from '../../../../components/Dropdown';
import { Drawer } from '../../../../components/Drawer';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import TextField from '../../../../components/TextField';
import { useBlurValidation } from '../../../../hooks/useBlurValidation';
import { WorkflowSubStepProps } from '../../config';
import { required } from '../../utils/fieldValidation';

export interface ContractInformationFormData {
  contractNumber: string;
  company: string;
  laborCategory: string;
  cmsRegion: string;
  requestJustification: string;
}

const cmsRegionOptions = [
  { label: '- Select an option -', value: '' },
  { label: 'N/A', value: 'n_a' },
  { label: 'CO (CENTRAL) Central Office', value: 'co_central' },
  { label: 'CO (LBDCO) Central Office', value: 'co_lbdco' },
  { label: 'CO (NORTH) Central Office', value: 'co_north' },
  { label: 'CO (SOUTH) Central Office', value: 'co_south' },
  { label: 'DC (COHEN) DC', value: 'dc_cohen' },
  { label: 'DC (HHS) DC', value: 'dc_hhs' },
  { label: 'R1 (JFKBOS) Boston', value: 'r1_jfkbos' },
  { label: 'R10 (BLNCH) Seattle', value: 'r10_blnch' },
  { label: 'R2 (JKJNYC) New York', value: 'r2_jkjnyc' },
  { label: 'R3 (PHIPLB) Philadelphia', value: 'r3_phiplb' },
  { label: 'R4 (AFC) Atlanta', value: 'r4_afc' },
  { label: 'R5 (CHIICB) Chicago', value: 'r5_chiicb' },
  { label: 'R6 (DAL1301) Dallas', value: 'r6_dal1301' },
  { label: 'R7 (FOBKAN) Kansas City', value: 'r7_fobkan' },
  { label: 'R8 (DENCSB) Denver', value: 'r8_dencsb' },
  { label: 'R9 (HWTHRN) San Francisco', value: 'r9_hwthrn' },
];

function ContractInformationPage({ data, updateData, fieldErrors }: WorkflowSubStepProps<ContractInformationFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const baseTestId = 'contract-information-page';

  const {
    handleBlur,
    handleChange: handleValidationChange,
    getFieldError,
  } = useBlurValidation({
    externalErrors: fieldErrors,
    formData: data,
    rules: [
      { field: 'cmsRegion', label: 'CMS Region/Facility', validationRules: [required] },
      { field: 'requestJustification', label: 'Request Justification', validationRules: [required] },
    ],
  });

  const handleChange = (field: keyof ContractInformationFormData, value: string) => {
    updateData({
      ...data,
      [field]: value,
    });
    handleValidationChange(field, value);
  };

  return (
    <div data-testid={baseTestId}>
      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-contract-number`}>
        <TextField
          readOnly
          data-testid={`${baseTestId}-contract-number-field`}
          label='Contract Number'
          name='contractNumber'
          tooltipText='Your contract number as provided by your requestor. This is a unique identifier assigned to your contract by CMS.'
          value={data.contractNumber}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-company`}>
        <TextField
          readOnly
          data-testid={`${baseTestId}-company-field`}
          label='Company'
          name='company'
          tooltipText='The name of the company or organization you work for, as provided by your requestor. This is the legal entity name as it appears on your contract.'
          value={data.company}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-labor-category`}>
        <TextField
          readOnly
          data-testid={`${baseTestId}-labor-category-field`}
          label='Labor Category'
          name='laborCategory'
          tooltipText='Your labor category or job title as specified in your contract. This helps us understand your role and assign appropriate access permissions.'
          value={data.laborCategory}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-cms-region`}>
        <Dropdown
          required
          searchable
          data-testid={`${baseTestId}-cms-region-dropdown`}
          errorMessage={getFieldError('cmsRegion')}
          label='CMS Region/Facility'
          name='cmsRegion'
          options={cmsRegionOptions}
          tooltipText='The CMS region or facility where you will be working. This determines which systems and locations you will have access to.'
          value={data.cmsRegion || ''}
          onBlur={() => handleBlur('cmsRegion')}
          onChange={(value: string) => handleChange('cmsRegion', value)}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-request-justification`}>
        <TextField
          multiline
          required
          data-testid={`${baseTestId}-request-justification-field`}
          errorMessage={getFieldError('requestJustification')}
          label='Please provide details for why you need access to CMS systems and/or infrastructure.'
          name='requestJustification'
          rows={5}
          tooltipText='The reason should clearly state why you need access to CMS systems or facilities. Briefly explain how this access is connected to your job responsibilities.'
          value={data.requestJustification || ''}
          onBlur={() => handleBlur('requestJustification')}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleChange('requestJustification', e.target.value)}
        />
      </div>

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: Contract Information'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help with My Contract Details</h3>
          <p>
            Some of your contract details have been entered using data sent by your requestor. Please check this
            information and then complete the remaining required fields.
          </p>

          <h4 className='ds-u-margin-top--3'>Contract Number</h4>
          <p>
            This is the number for the contract you are associated with. Your requestor sent this data for you. If it is
            wrong, please contact your requestor.
          </p>

          <h4 className='ds-u-margin-top--3'>Company</h4>
          <p>
            This is the name of the company or organization you work for. Your requestor provided this information for
            you. If it is wrong, please contact your requestor.
          </p>

          <h4 className='ds-u-margin-top--3'>Labor Category</h4>
          <p>
            This is your job title listed in your contract. It helps us understand what you do and ensures you get the
            right access and permissions. Your requestor provided this information for you. If it&apos;s wrong, please
            contact your requestor.
          </p>

          <h4 className='ds-u-margin-top--3'>CMS Region/Facility</h4>
          <p>
            Select the CMS region or facility where you will be working. This is required to ensure you are granted
            access to the correct systems and locations.
          </p>
        </div>
      </Drawer>
    </div>
  );
}

export default ContractInformationPage;
